import React from 'react';
import config from './config';
import { store } from './store';
import {
  updateObjects,
  deleteObjects,
  unloadProject,
  updatePreferences,
  panelNeedsRefresh,
  setDirtyObjects,
  uiSet,
  longTaskStarted,
  longTaskComplete,
  setProjectModified,
  recalculateProject,
  getAllowedFeatures,
  setImportingAnp,
  updateAnpJobStatus,
} from './pages/mainframe/mainframe.reducer';
import { userMsg } from './reducers/socket_reducer';
import { batch } from 'react-redux';
import {
  viewshedCompleted,
  viewshedFailed,
  fetchViewsheds,
  isViewshedCreation,
  handleViewshedDeletions,
  setPendingViewsheds,
} from './pages/viewshed/viewshed.reducer';
import {
  addOorResults,
  addResults,
  calculationComplete,
  creatingComplete,
  incLinksCreatedCount,
  setWorkerError,
  resetResults,
  startCalculation,
  startCreatingLinks,
  syncSettings,
} from './pages/best_server/best_server.reducer';
import { refreshTokenQuietly } from './api';
import { PollFallback, socketHTTPBase } from './pollFallback';
import { setLocalJSON, makePlural } from './utils/useful_functions';
import { toast } from 'react-toastify';
import { Message } from 'semantic-ui-react';
import {
  BEST_SERVER_PROGRESS_MESSAGE,
  BEST_SERVER_PROGRESS_SUBMESSAGE,
} from 'src/app.constants';
import {
  calcTiltProgressUpdate,
  setCalcTiltResults,
  calcTiltStatusUpdate,
} from './reducers/calc_tilt.reducer';
import {
  exportSocketHandler,
  reportSocketHandler,
} from './pages/mainframe/reportHelpers';
import {
  deleteFromTable,
  refreshTable,
  refreshSiteGraphTable,
} from './components/tables/internal/TableQueryKeys';
import { queryClient } from './query-client';
import { PROJECTS_QUERY_KEY } from './pages/project/ProjectListItems';
import { customFieldsQueryKey } from './hooks/useCustomFields';
import {
  getLayersQueryKey,
  isGISLayerCreation,
  handleGISLayerDeletions,
} from './pages/map/gis/gisReducer';
import { getCanvas } from './utils/mapUtils';

// To enable websocket debugging
// localStorage.debug = '*';

// WebSocket close codes
const WS_CLOSE_CODES = {
  PROTOCOL_ERROR: 1002,
  ABNORMAL_CLOSURE: 1006,
  POLICY_VIOLATION: 1008,
};

const TOKEN_REFRESH_DELAY = 500; // ms to wait after token refresh before reconnecting
const INITIAL_RECONNECT_DELAY = 200; // ms
const DEFAULT_RECONNECT_DELAY = 1000; // ms
const MAX_TOKEN_REFRESH_ATTEMPTS = 2; // Maximum times to attempt token refresh before giving up
const TRANSPORT_RETRY_DELAY = 15000; // ms — slow background retry once a transport failure is detected
const MAX_RECONNECT_DELAY = 30000; // ms — backoff cap while the socket server is unreachable
const HEALTH_PROBE_TIMEOUT = 5000; // ms — give up on the reachability probe after this

const toWebSocketURL = (baseURL) => {
  // config.apiURL is http(s); convert to ws(s) for the reverse-proxied Go socket server.
  return baseURL.replace(/^http/, 'ws').replace(/\/+$/, '');
};

const buildWSURL = (room, projectId = null) => {
  if (!room) {
    console.warn('[Socket] Missing room for WebSocket URL');
    return null;
  }
  const base = toWebSocketURL(config.socketServer);

  const params = new URLSearchParams({ room });
  if (projectId) {
    params.set('project_id', projectId);
  }
  console.debug('WebSocket URL:', `${base}/ws?${params.toString()}`);
  return `${base}/ws?${params.toString()}`;
};

// Shared in-flight probe so simultaneous closes (user + project sockets)
// don't fire duplicate requests.
let healthProbe = null;

/*
 * Check whether the socket server host answers plain HTTP. The browser
 * reports every failed WebSocket handshake as a bare 1006 close — an auth
 * rejection, a down server and a WS-blocking proxy all look identical — so
 * this probe is what lets us tell "server unreachable" apart from
 * "WebSockets specifically are being blocked".
 *
 * mode: 'no-cors' because we only need reachability, not the response: the
 * promise resolves if ANY HTTP response comes back (even an opaque 404/502)
 * and rejects only on a network-level failure (down, DNS, refused, blocked).
 * This means the socket server needs no CORS headers on /health.
 */
const probeSocketServer = () => {
  if (!healthProbe) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), HEALTH_PROBE_TIMEOUT);
    healthProbe = fetch(`${socketHTTPBase()}/health`, {
      mode: 'no-cors',
      cache: 'no-store',
      signal: controller.signal,
    })
      .then(() => true)
      .catch(() => false)
      .finally(() => {
        clearTimeout(timer);
        healthProbe = null;
      });
  }
  return healthProbe;
};

/*
 * Return true if the action is raised after
 * a project export event is complete
 * (Google Earth or LINKPlanner project export
 */
const exportIsComplete = (action) => {
  return action === 'lpp' || action === 'kmz';
};

const reportIsComplete = (action) => {
  return action === 'report';
};

const permissionNotification = (action) => {
  return action === 'PERMISSION_CHANGE';
};

const closeProject = () => {
  store.dispatch(unloadProject());
  store.dispatch(uiSet({ locked: false }));
};

const isDifferentUser = (email, currentUserEmail) => {
  return (
    email &&
    currentUserEmail &&
    email.toLowerCase() !== currentUserEmail.toLowerCase()
  );
};

const handleEditorsChange = (data) => {
  const previousEditors = store.getState().mainFrame.editors || [];
  const newEditors = data.people || [];
  const currentUserEmail = store.getState().mainFrame.email;

  const addedEditors = newEditors.filter(
    (email) =>
      !previousEditors.includes(email) &&
      isDifferentUser(email, currentUserEmail)
  );

  const removedEditors = previousEditors.filter(
    (email) =>
      !newEditors.includes(email) && isDifferentUser(email, currentUserEmail)
  );

  addedEditors.forEach((email) => {
    toast.info(`${email} joined the project`, { autoClose: 3000 });
  });

  removedEditors.forEach((email) => {
    toast.info(`${email} left the project`, { autoClose: 3000 });
  });

  store.dispatch(uiSet({ editors: newEditors }));
};

const handleProjectClosed = (data) => {
  const currentUserEmail = store.getState().mainFrame.email;

  closeProject();

  if (data?.action) {
    const messages = {
      discard: `Project changes were discarded by ${data.name} <${data.email}>`,
      delete: `Project was deleted by ${data.name} <${data.email}>`,
    };

    if (
      isDifferentUser(data.email, currentUserEmail) &&
      data?.name &&
      messages[data.action]
    ) {
      toast.info(messages[data.action], { autoClose: 3000 });
    }
  }
};

const handleProjectSaved = (data) => {
  const currentUserEmail = store.getState().mainFrame.email;

  store.dispatch(
    uiSet({
      locked: false,
      projectModified: false,
    })
  );

  if (isDifferentUser(data.email, currentUserEmail) && data?.name) {
    toast.info(`Project was saved by ${data.name} <${data.email}>`, {
      autoClose: 3000,
    });
  }
};

const handleProjectMessage = (data) => {
  const currentUserEmail = store.getState().mainFrame.email;

  if (
    isDifferentUser(data.email, currentUserEmail) &&
    data?.name &&
    data?.message
  ) {
    toast.info(
      <div>
        <b>
          {data.name} <br />
          &lt;{data.email}&gt;
        </b>
        <div>{data.message}</div>
      </div>,
      {
        autoClose: 3000,
      }
    );
  }
};

const handleBestServerAction = (action, data) => {
  switch (action) {
    case 'best_server_result':
      store.dispatch(addResults(data));
      break;
    case 'best_server_started':
      batch(() => {
        store.dispatch(resetResults());
        store.dispatch(startCalculation());
        store.dispatch(syncSettings(data.settings));
        store.dispatch(
          longTaskStarted({
            heading: BEST_SERVER_PROGRESS_MESSAGE,
            message: BEST_SERVER_PROGRESS_SUBMESSAGE,
          })
        );
      });
      break;
    case 'best_server_oor':
      store.dispatch(addOorResults(data));
      break;
    case 'best_server_worker_error':
      batch(() => {
        store.dispatch(setWorkerError(true));
        store.dispatch(longTaskComplete());
      });
      break;
    case 'best_server_complete':
      batch(() => {
        store.dispatch(calculationComplete());
        store.dispatch(longTaskComplete());
      });
      break;
    case 'best_server_cancelled':
      batch(() => {
        store.dispatch(calculationComplete({ complete: false }));
        store.dispatch(longTaskComplete());
        store.dispatch(resetResults());
      });
      break;
    case 'best_server_deleted':
      store.dispatch(resetResults());
      break;
    case 'best_server_creating_started':
      batch(() => {
        store.dispatch(startCreatingLinks(true));
        store.dispatch(
          longTaskStarted({
            heading: BEST_SERVER_PROGRESS_MESSAGE,
            message:
              'Creating links from best server, editing the project is temporarily disabled',
          })
        );
      });
      break;
    case 'best_server_creating_complete':
      batch(() => {
        store.dispatch(creatingComplete());
        store.dispatch(longTaskComplete());
      });
      break;
    case 'best_server_created_sm':
      store.dispatch(incLinksCreatedCount(data.objects[0]));
      break;
    default:
      return false;
  }
  return true;
};

/*
 * Handle create/update actions on features (sites, links, etc)
 */
const handleFeatureActions = (data) => {
  const projectId = store.getState().mainFrame.projectId;
  if (data?.objects) {
    // object update
    store.dispatch(updateObjects(data));
    if (data.objects && data.objects.length > 0) {
      // The objects array can contain different feature kinds
      const idsPerKind = {};
      data.objects.forEach((o) => {
        let columns = o.columns;
        if (o.columns && !Array.isArray(o.columns)) {
          columns = [o.columns];
        }

        if (!idsPerKind[o.kind]) {
          idsPerKind[o.kind] = [];
        }
        if (columns) {
          columns.forEach((c) => idsPerKind[o.kind].push(c));
        }
      });
      for (const kind in idsPerKind) {
        refreshTable(kind, projectId, idsPerKind[kind]);
      }
    }
  }

  if (data.action === 'site_rename') {
    // TODO: This really shouldn't be necessary. The site panel
    // should be updated to invalidate the query and remove
    // the site reducer
    const { proj_id, site_id } = data;
    refreshSiteGraphTable(proj_id, site_id);
  }
};

const handleUpdateMessage = (data) => {
  const projectId = store.getState().mainFrame.projectId;

  // Handle GIS layer additions
  if (isGISLayerCreation(data)) {
    if (projectId) {
      const queryKey = getLayersQueryKey(projectId);
      queryClient.refetchQueries({ queryKey });
    }
    return;
  }

  // Handle viewshed layer additions
  if (isViewshedCreation(data)) {
    if (projectId) {
      const hasConfig = data?.config && Array.isArray(data?.objects);
      if (hasConfig) {
        try {
          const key = data.objects[0]?.key;
          const viewsheds = data.objects.map((o) => ({
            id: o.id,
            site_name: o.site_name,
          }));

          if (key && viewsheds.length > 0) {
            // Only mark as pending for the user who created them
            store.dispatch(
              setPendingViewsheds({
                data: data.config,
                key,
                viewsheds,
              })
            );
          } else {
            store.dispatch(fetchViewsheds(projectId));
          }
        } catch (e) {
          store.dispatch(fetchViewsheds(projectId));
        }
      }
    }
    return;
  }

  // Handle best server actions
  if (data.action && data.action.startsWith('best_server_')) {
    if (handleBestServerAction(data.action, data)) {
      return;
    }
  }

  if (data.action === 'calc_tilt_progress') {
    console.log(data);
    store.dispatch(calcTiltProgressUpdate(data.message));
  } else if (data.action === 'calc_tilt_complete') {
    store.dispatch(calcTiltStatusUpdate('completed'));
    store.dispatch(setCalcTiltResults(data.message));
  } else if (data.action === 'LIDAR_REQUEST_FAILED') {
    console.log(data);
    toast(
      <Message error>
        Missing high resolution profile data for {data.objects.length}{' '}
        {makePlural(data.objects.length, 'profile', 'profiles')}
      </Message>,
      {
        autoClose: 2000,
      }
    );
  } else if (data.action === 'anp_update') {
    store.dispatch(setProjectModified(true));
  } else if (data.action === 'bom') {
    store.dispatch(setProjectModified(true));
  } else if (data.action === 'custom_field_definition_change') {
    store.dispatch(setProjectModified(true));
    if (projectId) {
      queryClient.invalidateQueries({
        queryKey: customFieldsQueryKey(projectId),
      });
    }
  } else if (data.action === 'network_bom_partial') {
    store.dispatch(uiSet({ networkBomReadyTick: Date.now() }));
  } else if (data.action === 'network_bom_ready') {
    store.dispatch(uiSet({ networkBomReadyTick: Date.now() }));
  } else if (data.action === 'project_recalc_complete') {
    store.dispatch(recalculateProject({ complete: true }));
    store.dispatch(setImportingAnp(false));
  } else if (data.action === 'ANP_JOB_UPDATE') {
    store.dispatch(updateAnpJobStatus(data));
    const anpStatus = data.status;
    if (
      anpStatus === 'completed' ||
      anpStatus === 'failed' ||
      anpStatus === 'cancelled'
    ) {
      store.dispatch(longTaskComplete());
    }
  } else {
    handleFeatureActions(data);
  }
};

/*
 * Connection-UI policy for the long-poll fallback: what a healthy or broken
 * fallback means for the connected/transportError state that drives the
 * MainFrame banner. Shared by the project and user handlers (the policy is
 * global, not per-room), and kept here with the rest of the code that
 * manipulates those flags — PollFallback itself is transport-only.
 */
const onPollHealthy = () => {
  // The fallback delivers live updates, so the app is effectively
  // connected: clear the transport error UI. Only dispatch on change to
  // avoid a redux update every ~25s poll cycle.
  const mainFrame = store.getState().mainFrame;
  if (!mainFrame.connected || mainFrame.transportError) {
    store.dispatch(uiSet({ connected: true, transportError: false }));
  }
};

const onPollBroken = (authFailure = false) => {
  if (authFailure) {
    // The server keeps rejecting the poll with 401 even after quiet token
    // refreshes: an auth/permission problem (dead session, revoked room
    // access), not a blocked transport. Don't show the transport-error UI —
    // it tells the user their session is still valid. Genuine session
    // expiry is handled by the REST 401 path, and revoked project access
    // arrives as a PERMISSION_CHANGE user message.
    store.dispatch(uiSet({ connected: false, transportError: false }));
    return;
  }
  // Polling can't reach the server either: surface the transport error UI
  // — the connection banner in MainFrame — while the loop keeps retrying
  // in the background and clears it on recovery.
  store.dispatch(uiSet({ connected: false, transportError: true }));
};

class BaseSocketHandler {
  constructor(roomType, buildWSURL) {
    this.socket = null;
    this.room = null;
    this.roomType = roomType; // 'project' or 'user'
    this.buildWSURL = buildWSURL;
    this.reconnectDelayMs = INITIAL_RECONNECT_DELAY;
    this.knownVersion = window.runtime.version;
    this.knownChangeset = window.runtime.changeset;
    this.projectId = null;
    this._connectionError = false;
    this._tokenRefreshed = false;
    this._tokenRefreshAttempts = 0;
    // Long-poll fallback transport (engaged when WebSockets are blocked but
    // the socket server is reachable over HTTP). The class owns the polling
    // mechanics; the callbacks own what healthy/broken mean for the UI.
    this.poll = new PollFallback({
      label: roomType,
      onEvent: (event, data) => this._handleEvent(event, data, null),
      onHealthy: onPollHealthy,
      onBroken: onPollBroken,
    });
  }

  _fullRoomName() {
    return this.roomType === 'project'
      ? `PROJECT-${this.room}`
      : `USER-${this.room}`;
  }

  _isAuthFailure(event, hasOpened) {
    // Auth is enforced during the HTTP handshake: the Go socket server returns
    // 401 *before* upgrading, so a genuine auth rejection closes with an
    // abnormal code (1006) having NEVER opened. A socket that successfully
    // opened and then closed (idle timeout, proxy/server drop, network blip,
    // laptop sleep) is a transient disconnect — NOT an auth failure. Treating
    // those as auth failures triggers needless token refreshes and can log the
    // user out, so only suspect auth when the handshake itself never opened.
    // hasOpened is captured per-socket by _wireSocket so a delayed onclose from
    // a superseded socket can't read a newer attempt's state.
    if (hasOpened) {
      return false;
    }
    return (
      this._connectionError ||
      (event &&
        (!event.wasClean ||
          event.code === WS_CLOSE_CODES.ABNORMAL_CLOSURE ||
          event.code === WS_CLOSE_CODES.POLICY_VIOLATION ||
          event.code === WS_CLOSE_CODES.PROTOCOL_ERROR))
    );
  }

  _reconnect(delay = this.reconnectDelayMs) {
    setTimeout(() => {
      this.connect(this.room, this.projectId);
    }, delay);
  }

  _reconnectWithTokenRefresh(event) {
    this._tokenRefreshAttempts++;

    if (this._tokenRefreshAttempts > MAX_TOKEN_REFRESH_ATTEMPTS) {
      console.error(
        `[Socket] Max token refresh attempts (${MAX_TOKEN_REFRESH_ATTEMPTS}) exceeded — treating as a transport failure.`
      );
      // We've refreshed the access token the maximum number of times and the
      // socket STILL never reaches OPEN on the client — while the health
      // probe confirms the socket server host IS answering plain HTTP (this
      // path is only entered when the probe succeeds). This is NOT an expired
      // session: the refreshes above succeed (/refresh returns 200) and
      // genuine session expiry is handled authoritatively by the REST 401
      // path. HTTP works but WebSockets don't — a VPN, firewall or corporate
      // proxy breaking the WebSocket upgrade/return path. Report it as a
      // connectivity problem, not a login problem, and don't clear the room
      // so a background retry (or window focus) can self-heal.
      this._tokenRefreshed = false;
      this._handleTransportFailure(event);
      return;
    }

    console.debug(
      `[Socket] Auth failure suspected (code: ${event?.code}), refreshing tokens before reconnect (attempt ${this._tokenRefreshAttempts}/${MAX_TOKEN_REFRESH_ATTEMPTS})`
    );
    this._tokenRefreshed = true;

    // Use the quiet refresh so a failed/unavailable refresh never logs the
    // user out from the socket path — we just retry the reconnect (up to the
    // max-attempts cap, which surfaces a non-blocking notice). Genuine session
    // expiry is still handled by the REST 401 path.
    refreshTokenQuietly()
      .then((token) => {
        if (!token) {
          // The refresh itself failed (resolves null): an auth/session or API
          // problem, NOT a blocked WebSocket transport — the transport-failure
          // UI would wrongly tell the user their session is still valid. Back
          // off and retry quietly, leaving genuine session expiry to the REST
          // 401 path. Resetting the attempt counter keeps failed refreshes
          // from ever escalating to the transport-failure messaging (that
          // escalation must only fire when refreshes SUCCEED and the socket
          // still won't open).
          console.warn(
            '[Socket] Token refresh failed — backing off before reconnect'
          );
          this._tokenRefreshed = false;
          this._tokenRefreshAttempts = 0;
          this._retryQuietly();
          return;
        }
        setTimeout(() => {
          console.debug('[Socket] Attempting reconnection after token refresh');
          this._tokenRefreshed = false;
          this.connect(this.room, this.projectId);
        }, TOKEN_REFRESH_DELAY);
      })
      .catch((err) => {
        console.error('[Socket] Token refresh failed:', err);
        this._tokenRefreshed = false;
        // Will try again on next reconnect up to max attempts
        this._reconnect();
      });
  }

  _handleTransportFailure(event) {
    console.warn(
      `[Socket] Transport failure (code: ${event?.code}) — WebSocket blocked; falling back to HTTP long-polling.`
    );
    // Only mark the UI disconnected if the fallback isn't already delivering
    // events (this method re-fires on every failed background WS retry).
    if (!this.poll.healthy) {
      store.dispatch(uiSet({ connected: false, transportError: true }));
    }

    // Work around the blocked transport: deliver events over HTTP
    // long-polling (same origin/auth as the WS handshake), while retrying a
    // real WebSocket in the background so we upgrade back when possible
    // (also retried on window focus).
    this.poll.start(this._fullRoomName());
    this._reconnect(TRANSPORT_RETRY_DELAY);
  }

  // Back off and retry without the transport-error UI: used when the failure
  // is not a WebSocket-transport problem (server down, refresh failing), so
  // the generic connection banner covers the messaging instead.
  _retryQuietly() {
    store.dispatch(uiSet({ transportError: false }));
    this._reconnect();
    // Back off so a struggling server isn't hammered with reconnects; onopen
    // resets the delay when the connection recovers.
    this.reconnectDelayMs = Math.min(
      this.reconnectDelayMs * 2,
      MAX_RECONNECT_DELAY
    );
  }

  _handleServerUnreachable() {
    console.warn(
      `[Socket] ${this.roomType} socket server unreachable over HTTP — retrying in ${this.reconnectDelayMs}ms`
    );
    this._retryQuietly();
  }

  _isOpen() {
    return this.socket && this.socket.readyState === WebSocket.OPEN;
  }

  connect(roomId, projectId = null) {
    if (!roomId) return;
    if (this._isOpen()) return;
    if (
      this.room === roomId &&
      this.socket &&
      (this.socket.readyState === WebSocket.OPEN ||
        this.socket.readyState === WebSocket.CONNECTING)
    ) {
      // abort if the connection is already open
      return;
    }
    this.room = roomId;
    if (this.roomType === 'project') {
      this.projectId = projectId;
    }
    const wsUrl =
      this.roomType === 'project'
        ? this.buildWSURL(`PROJECT-${roomId}`, projectId)
        : this.buildWSURL(`USER-${roomId}`);
    if (!wsUrl) return;
    const projectTag =
      this.roomType === 'project' && this.projectId
        ? ` project_id=${this.projectId}`
        : '';
    console.debug(`[Socket] Connecting to ${wsUrl}${projectTag}`);
    const ws = new WebSocket(wsUrl);
    this.socket = ws;
    this._wireSocket(ws);
  }

  disconnect(clearRoom = false) {
    // Stop any long-poll fallback: either the room is going away, or a
    // fresh connect() is about to re-run transport detection.
    this.poll.stop();
    if (this.socket) {
      try {
        const projectTag =
          this.roomType === 'project' && this.projectId
            ? ` project_id=${this.projectId}`
            : '';
        console.debug(
          `[Socket] Disconnecting from room: ${this.roomType}-${this.room}${projectTag}`
        );
        this.socket._intentionalClose = true; // mark this socket before closing
        this.socket.close();
      } catch (e) {
        console.warn('[Socket] Error during disconnect:', e);
      }
    }
    this.socket = null;
    if (clearRoom) {
      this.room = null;
      if (this.roomType === 'project') {
        this.projectId = null;
      }
    }
  }

  _wireSocket(ws) {
    // Per-socket state: a delayed onclose/onerror from a superseded socket must
    // not read a newer connection attempt's flags.
    let hasOpened = false;

    ws.onopen = () => {
      console.debug(`[Socket] Connected: ${this.roomType}-${this.room}`);
      hasOpened = true;
      this.reconnectDelayMs = DEFAULT_RECONNECT_DELAY;
      this._tokenRefreshed = false;
      this._tokenRefreshAttempts = 0; // Reset counter on successful connection
      // A real WebSocket is up again: the long-poll fallback (if running)
      // is no longer needed.
      this.poll.stop();
      // Recovered from any prior transport failure: clear the notice.
      store.dispatch(uiSet({ connected: true, transportError: false }));
      store.dispatch(getAllowedFeatures());
    };

    ws.onclose = (event) => {
      const wasIntentional = ws._intentionalClose === true;

      console.warn(`[Socket] Disconnected: ${this.roomType}-${this.room}`, {
        code: event?.code,
        reason: event?.reason,
        wasClean: event?.wasClean,
        connectionError: this._connectionError,
        tokenRefreshed: this._tokenRefreshed,
        intentional: wasIntentional,
      });

      // Don't change UI state or reconnect if room was cleared or this was intentional
      if (!this.room || wasIntentional) {
        // Clear any stale error marker; this was an expected/clean close path
        this._connectionError = false;
        return;
      }

      // Only mark disconnected if BOTH sockets are down — a healthy
      // long-poll fallback counts as connected (this close is likely a
      // failed background WS upgrade attempt).
      const anySocketOpen =
        (socketManager?.project &&
          (socketManager.project._isOpen() ||
            socketManager.project.poll.healthy)) ||
        (socketManager?.user &&
          (socketManager.user._isOpen() || socketManager.user.poll.healthy));
      if (!anySocketOpen) {
        store.dispatch(uiSet({ connected: false }));
      }

      const isAuthFailure = this._isAuthFailure(event, hasOpened);
      console.debug(`[Socket] Auth failure suspected: ${isAuthFailure}`);
      this._connectionError = false; // Clear the error flag

      if (isAuthFailure && !this._tokenRefreshed) {
        // A handshake that never opened is EITHER an auth rejection or a
        // transport failure — the browser reports both as a bare 1006 close.
        // Probe the socket server over plain HTTP to tell them apart before
        // deciding what to do.
        probeSocketServer().then((reachable) => {
          if (!this.room) return; // room cleared while probing
          if (reachable) {
            // The host answers HTTP, so the WS handshake itself was rejected
            // or broken: try a token refresh. If refreshing doesn't fix it,
            // _reconnectWithTokenRefresh escalates to a transport failure
            // (WebSockets specifically are being blocked).
            this._reconnectWithTokenRefresh(event);
          } else {
            // No HTTP response at all: the socket server is down or
            // unreachable (deploy, restart, network out). A token refresh
            // can't help — retry quietly with backoff and let the generic
            // connection banner cover the messaging.
            this._handleServerUnreachable();
          }
        });
      } else {
        if (this._tokenRefreshed) {
          console.debug('[Socket] Skipping token refresh - already attempted');
        }
        this._reconnect();
      }
    };

    ws.onmessage = (evt) => {
      try {
        const parsed = JSON.parse(evt.data);
        const eventName = parsed?.event;
        const data = parsed?.data;
        if (eventName) {
          this._handleEvent(eventName, data, ws);
        }
      } catch (e) {
        console.debug('SOCKET: failed to parse message', e, evt.data);
      }
    };

    ws.onerror = (err) => {
      console.warn(`[Socket] ${this.roomType} connection error`, err);
      // Mark that an error occurred - likely an auth failure if connection never opened
      this._connectionError = true;
    };
  }

  // To be implemented by subclasses
  _handleEvent(eventName, data, ws) {}
}

class ProjectSocketHandler extends BaseSocketHandler {
  constructor(buildWSURL) {
    super('project', buildWSURL);
  }
  _handleEvent(eventName, data, ws) {
    switch (eventName) {
      case 'editors':
        console.debug('SOCKET: editors', data);
        handleEditorsChange(data);
        break;
      case 'update':
        console.debug('SOCKET: on_update');
        handleUpdateMessage(data);
        break;
      case 'refresh':
        console.debug('SOCKET: on_refresh');
        {
          const { panels, dirtyObjs } = data;
          batch(() => {
            store.dispatch(
              panelNeedsRefresh({
                panels,
                status: true,
              })
            );
            if (dirtyObjs) {
              store.dispatch(
                setDirtyObjects({
                  value: false,
                  objects: dirtyObjs,
                })
              );
            } else {
              store.dispatch(uiSet({ bulkEditSelectedRows: [] }));
            }
          });
        }
        break;
      case 'viewshed':
        console.debug('SOCKET: viewshed event received', data);
        if (data.status === 'done') {
          console.debug('SOCKET: dispatching viewshedCompleted', {
            id: data.id,
            key: data.key,
          });
          const showInMap = data.user_id === store.getState().mainFrame.userId;
          store.dispatch(viewshedCompleted({ ...data, showInMap }));
        } else {
          console.debug('SOCKET: dispatching viewshedFailed', {
            id: data.id,
            status: data.status,
          });
          store.dispatch(viewshedFailed(data));
        }
        break;
      case 'delete':
        console.debug('SOCKET: on_delete');
        {
          const kind = data?.kind;
          const projectId = store.getState().mainFrame.projectId;

          // Handle GIS layer deletions
          if (kind === 'gis_layer' && projectId) {
            handleGISLayerDeletions(data.objects);
            const queryKey = getLayersQueryKey(projectId);
            queryClient.refetchQueries({ queryKey });
            break;
          }

          // Handle viewshed layer deletions
          if (kind === 'viewshed' && projectId) {
            handleViewshedDeletions(data.objects);
            store.dispatch(fetchViewsheds(projectId));
            break;
          }

          deleteFromTable(kind, projectId, data.objects);
          store.dispatch(deleteObjects(data));
        }
        break;
      case 'closed':
        console.debug('SOCKET: on_closed');
        handleProjectClosed(data);
        break;
      case 'saved':
        console.debug('SOCKET: on_saved');
        handleProjectSaved(data);
        break;
      case 'project_message':
        console.debug('SOCKET: on_project_message');
        handleProjectMessage(data);
        break;
      default:
        console.debug('SOCKET: unhandled event', eventName, data);
    }
  }
}

class UserSocketHandler extends BaseSocketHandler {
  constructor(buildWSURL) {
    super('user', buildWSURL);
  }
  _handleEvent(eventName, data, ws) {
    console.debug('UserSocketHandler event:', eventName, data);
    switch (eventName) {
      case 'version':
        console.log('VERSION MESSAGE', data);
        if (
          data?.version !== this.knownVersion ||
          data?.changeset !== this.knownChangeset
        ) {
          this.knownVersion = data.version;
          this.knownChangeset = data.changeset;
          console.info('Updating...');
          setLocalJSON('cn.lp.hasUpdated', true);
          store.dispatch(uiSet({ isUpdating: true }));
          setTimeout(() => window.location.reload(), 20000);
        }
        break;
      case 'user_message':
        console.debug('SOCKET: on_user_message:', data);
        if (data) {
          const { action, status } = data;
          if (status === 'success' && action === 'account') {
            store.dispatch(updatePreferences(data));
          } else if (action === 'LINKPLANNER_IMPORT') {
            if (status === 'WARNING') {
              toast(<Message error>{data.message}</Message>, {
                autoClose: true,
              });
            }
            // trigger open project when the project ID is included
            // and the status === "SUCCESS"
            store.dispatch(
              userMsg({
                status: data.status?.toUpperCase(),
                message: data.message,
                action: action,
                projectId: data.project_id || '',
                warnings: data.warnings || [],
              })
            );
          } else if (exportIsComplete(action)) {
            exportSocketHandler(data);
          } else if (reportIsComplete(action)) {
            reportSocketHandler(data);
          } else if (permissionNotification(action)) {
            queryClient.invalidateQueries({ queryKey: PROJECTS_QUERY_KEY });
            if (data.project_id === store.getState().mainFrame.projectId) {
              closeProject();
            }
          } else {
            store.dispatch(userMsg(data));
          }
        }
        break;
      default:
        console.debug('SOCKET: unhandled event', eventName, data);
    }
  }
}

// Main manager to coordinate both
const socketManager = {
  project: new ProjectSocketHandler(buildWSURL),
  user: new UserSocketHandler(buildWSURL),

  // Legacy compatibility for code expecting .projectSocket and .userSocket
  get projectSocket() {
    return this.project;
  },
  get userSocket() {
    return this.user;
  },

  connectProject(projectId) {
    this.project.disconnect(false);
    this.project.connect(projectId, projectId);
  },
  disconnectProject() {
    this.project.disconnect(true);
  },
  connectUser(userId) {
    this.user.disconnect(false);
    this.user.connect(userId);
  },
  disconnectUser() {
    this.user.disconnect(true);
  },
  sendProjectMessage(msg) {
    if (this.project._isOpen()) {
      this.project.socket.send(msg);
    }
  },
  sendUserMessage(msg) {
    if (this.user._isOpen()) {
      this.user.socket.send(msg);
    }
  },
};

window.addEventListener('focus', () => {
  const projectSocket = socketManager.projectSocket;
  const userSocket = socketManager.userSocket;
  const projectDown =
    projectSocket &&
    projectSocket.socket &&
    projectSocket.socket.readyState !== WebSocket.OPEN;
  const userDown =
    userSocket &&
    userSocket.socket &&
    userSocket.socket.readyState !== WebSocket.OPEN;

  // Reconnect directly on focus when there is a room to reconnect to. We do
  // NOT refresh the token here: the reconnect handshake validates the access
  // token (the `token` cookie), and only if it fails (the socket never opens)
  // does the onclose auth path perform a quiet refresh. This keeps focus from
  // being a needless /refresh source and avoids refreshing when there's
  // nothing to reconnect to (e.g. after the room was cleared).
  // While the long-poll fallback is active, leave it alone: it already
  // delivers live updates, and the background WS retry handles upgrading
  // back — a focus-triggered disconnect would needlessly restart transport
  // detection (probe + token refreshes).
  if (projectDown && projectSocket.room && !projectSocket.poll.polling) {
    projectSocket.disconnect(false);
    projectSocket.connect(projectSocket.room, projectSocket.projectId);
  }
  if (userDown && userSocket.room && !userSocket.poll.polling) {
    userSocket.disconnect(false);
    userSocket.connect(userSocket.room);
  }
});

export default socketManager;
